import { useState, useEffect } from 'react';
import { LIVE_COLLECTIONS as SCHEMA_LIVE_COLLECTIONS } from '@vibesites/schema';
import { getLiveDataUrl } from '../live-data';
import { useProperty } from './context';

const LIVE_COLLECTIONS: ReadonlySet<string> = new Set(SCHEMA_LIVE_COLLECTIONS);

const liveCache = new Map<string, { data: unknown[]; timestamp: number }>();
const LIVE_CACHE_TTL = 60_000;
const LIVE_TIMEOUT_MS = 10_000;

type LiveResult = { ok: true; rows: unknown[] } | { ok: false };

const inflightRequests = new Map<string, Promise<LiveResult>>();

export type LiveCollection<T> =
  /** Not a live collection, or no worker URL or property id. */
  | { readonly status: 'static' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly rows: T[] }
  /** Non-2xx, a body that is not an array, a network error, or a 10 second timeout. Not cached. */
  | { readonly status: 'failed'; readonly lastRows: T[] | null };

/**
 * Fetch a live collection from the edge worker.
 * Static when the collection is not live or the worker URL or property id is missing.
 * Ready rows replace the baked collection. A failure keeps lastRows from an earlier
 * success, including a cache entry that has already expired.
 */
export function useLiveCollection<T>(collection: string): LiveCollection<T> {
  const property = useProperty();
  const propertyId = property.multihub_property_id;
  const workerUrl = getLiveDataUrl();
  const canFetch = LIVE_COLLECTIONS.has(collection) && Boolean(workerUrl) && Boolean(propertyId);

  const [state, setState] = useState<LiveCollection<T>>(() => initialState(canFetch, propertyId, collection));

  useEffect(() => {
    if (!canFetch || !propertyId || !workerUrl) return;

    const cacheKey = `${propertyId}:${collection}`;
    const cached = liveCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < LIVE_CACHE_TTL) {
      setState({ status: 'ready', rows: cached.data as T[] });
      return;
    }

    const lastRows = cached ? cached.data as T[] : null;
    setState({ status: 'loading' });

    let request = inflightRequests.get(cacheKey);
    if (!request) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), LIVE_TIMEOUT_MS);
      request = fetch(`${workerUrl}/collections/${propertyId}/${collection}`, { signal: controller.signal })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
        .then((result) => {
          if (!Array.isArray(result)) throw new Error('not an array');
          liveCache.set(cacheKey, { data: result, timestamp: Date.now() });
          return { ok: true as const, rows: result };
        })
        .catch(() => ({ ok: false as const }))
        .finally(() => {
          clearTimeout(timer);
          inflightRequests.delete(cacheKey);
        });
      inflightRequests.set(cacheKey, request);
    }

    let cancelled = false;
    request.then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setState({ status: 'ready', rows: result.rows as T[] });
        return;
      }
      setState({ status: 'failed', lastRows });
    });

    return () => { cancelled = true; };
  }, [canFetch, workerUrl, propertyId, collection]);

  if (!canFetch) return { status: 'static' };
  return state;
}

function initialState<T>(canFetch: boolean, propertyId: string | null | undefined, collection: string): LiveCollection<T> {
  if (!canFetch || !propertyId) return { status: 'static' };
  const cached = liveCache.get(`${propertyId}:${collection}`);
  if (cached && Date.now() - cached.timestamp < LIVE_CACHE_TTL) {
    return { status: 'ready', rows: cached.data as T[] };
  }
  return { status: 'loading' };
}
